import { Link } from "wouter";
import { ArrowLeft, Gift, Star, Award, TrendingUp, Sparkles, CheckCircle2, Crown, Zap, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

export default function LoyaltyBenefits() {
  const { user } = useAuth();

  const tiers = [
    {
      name: "Bronze",
      icon: Star,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
      threshold: "Başlangıç",
      benefits: [
        "Her yıkamada 1 puan kazan",
        "5 puan = 1 bedava yıkama",
        "Özel kampanyalarda öncelik",
      ]
    },
    {
      name: "Silver",
      icon: Award,
      color: "text-gray-600 dark:text-gray-400",
      bgColor: "bg-gray-100 dark:bg-gray-900/20",
      threshold: "₺1.000+ harcama",
      benefits: [
        "Tüm Bronze avantajlar",
        "Doğum günü hediyesi",
        "Erken randevu imkanı",
        "%5 ek indirim",
      ]
    },
    {
      name: "Gold",
      icon: Crown,
      color: "text-yellow-600 dark:text-yellow-400",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
      threshold: "₺5.000+ harcama",
      benefits: [
        "Tüm Silver avantajlar",
        "VIP randevu hattı",
        "%10 ek indirim",
        "Ücretsiz detaylı temizlik (yılda 1)",
        "Hediye çekilişlerinde 2x şans",
      ]
    }
  ];

  const features = [
    {
      icon: Gift,
      title: "Puan Kazan, Hediye Kazan",
      description: "Her yıkamada 1 puan kazanırsınız. 5 puan biriktirdiğinizde bir sonraki yıkamanız bizden!",
      highlight: "5 puan = 1 bedava yıkama"
    },
    {
      icon: TrendingUp,
      title: "Seviye Atla, Daha Fazla Kazan",
      description: "Harcama miktarınız arttıkça seviyeniz yükselir. Her seviyede daha fazla avantaj sizi bekliyor.",
      highlight: "3 farklı seviye"
    },
    {
      icon: Sparkles,
      title: "Özel Hediyeler ve Sürprizler",
      description: "Sadakat kartınızı doldurduğunuzda şans çarkımızda özel hediyeler kazanın. Ayrıcalıklı kampanyalardan haberdar olun.",
      highlight: "Sürpriz hediyeler"
    },
    {
      icon: Zap,
      title: "Anlık Bildirimler",
      description: "Puanlarınız, seviyeniz ve özel fırsatlar hakkında anında bilgilendirilirsiniz. Hiçbir avantajı kaçırmayın!",
      highlight: "Anlık takip"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href={user ? "/customer" : "/"}>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="button-back">
                <ArrowLeft className="w-4 h-4" />
                Geri
              </Button>
            </Link>
            {!user && (
              <Link href="/book">
                <Button size="sm" data-testid="button-join">
                  <Heart className="w-4 h-4 mr-2" />
                  Hemen Kayıt Ol
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
              <Gift className="w-5 h-5" />
              <span className="font-semibold">Sadakat Programı</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-foreground">
              Yıka, <span className="text-primary">Puan Kazan</span>, Hediye Al!
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Her yıkama bir fırsat! Sadakat programımıza katılın, puanlarınızı biriktirin ve özel avantajlardan faydalanın.
            </p>
            {!user && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                <Link href="/book">
                  <Button size="lg" className="text-lg px-8" data-testid="button-cta-register">
                    <Heart className="w-5 h-5 mr-2" />
                    Ücretsiz Kayıt Ol
                  </Button>
                </Link>
                <p className="text-sm text-muted-foreground">
                  İlk randevunuzla sadakat puanı kazanmaya başlayın!
                </p>
              </div>
            )}
          </div>

          {/* Key Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="hover-elevate">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                    <CardDescription className="text-sm">{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="secondary" className="font-semibold">
                      {feature.highlight}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Tier System */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Seviye Sistemi</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Harcama miktarınız arttıkça seviyeniz yükselir ve daha fazla avantaja sahip olursunuz
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {tiers.map((tier, index) => {
                const TierIcon = tier.icon;
                return (
                  <Card key={index} className={`${tier.bgColor} border-2 hover-elevate transition-all`} data-testid={`tier-${tier.name.toLowerCase()}`}>
                    <CardHeader className="text-center">
                      <div className={`w-16 h-16 rounded-full ${tier.bgColor} flex items-center justify-center mx-auto mb-4`}>
                        <TierIcon className={`w-8 h-8 ${tier.color}`} />
                      </div>
                      <CardTitle className="text-2xl">{tier.name}</CardTitle>
                      <CardDescription className="text-base font-semibold">
                        {tier.threshold}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {tier.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className={`w-5 h-5 ${tier.color} shrink-0 mt-0.5`} />
                            <span className="text-sm">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Nasıl Çalışır?</h2>
              <p className="text-lg text-muted-foreground">3 basit adımda sadakat programına katılın</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center hover-elevate">
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                    1
                  </div>
                  <CardTitle>Kayıt Olun</CardTitle>
                  <CardDescription>
                    İlk randevunuzu alın ve otomatik olarak programa dahil olun. Telefon numaranız yeterli!
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="text-center hover-elevate">
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                    2
                  </div>
                  <CardTitle>Puan Kazanın</CardTitle>
                  <CardDescription>
                    Her yıkamada 1 puan kazanın. Puanlarınızı takip edin, seviyenizi yükseltin.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="text-center hover-elevate">
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                    3
                  </div>
                  <CardTitle>Hediye Kazanın</CardTitle>
                  <CardDescription>
                    5 puan = 1 bedava yıkama! Puanlarınızı kullanın veya biriktirin.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>

          {/* CTA Section */}
          {!user && (
            <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-12 text-center">
                <Gift className="w-16 h-16 text-primary mx-auto mb-6" />
                <h2 className="text-3xl font-bold mb-4">Hemen Başlayın!</h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  İlk randevunuzu alın ve sadakat programının tüm avantajlarından faydalanmaya başlayın. 
                  Kayıt olmak ücretsiz, puanlar sınırsız!
                </p>
                <Link href="/book">
                  <Button size="lg" className="text-lg px-8" data-testid="button-cta-final">
                    <Heart className="w-5 h-5 mr-2" />
                    Şimdi Randevu Al
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
